/** Proveedor de contexto: idioma, audiencia y tema en un solo sitio. */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { setLocale, initialLocale } from '../i18n'
import type { Audience, Locale, Theme } from '../lib/types'

const THEMES: Theme[] = ['light', 'dark', 'lapland']
const AUDIENCES: Audience[] = ['internal', 'public']

interface AppState {
  locale: Locale
  audience: Audience
  theme: Theme
  setLocale: (l: Locale) => void
  setAudience: (a: Audience) => void
  setTheme: (t: Theme) => void
}

const AppContext = createContext<AppState | null>(null)

function readTheme(): Theme {
  const saved = localStorage.getItem('lma.theme') as Theme | null
  return saved && THEMES.includes(saved) ? saved : 'dark'
}

function readLocale(): Locale {
  return initialLocale()
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement
  root.classList.remove('light', 'dark', 'lapland')
  root.classList.add(theme)
  root.style.colorScheme = theme === 'light' ? 'light' : 'dark'
  localStorage.setItem('lma.theme', theme)
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readLocale)
  const [audience, setAudience] = useState<Audience>('internal')
  // El tema arranca en el que pone el script de index.html, para que la
  // primera pintada y el estado de React coincidan y no haya un parpadeo.
  const [theme, setThemeState] = useState<Theme>(readTheme)

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const value = useMemo<AppState>(
    () => ({
      locale,
      audience,
      theme,
      setLocale: (l) => {
        setLocaleState(l)
        setLocale(l)
      },
      setAudience,
      setTheme: setThemeState,
    }),
    [locale, audience, theme],
  )

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export function useApp(): AppState {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp debe usarse dentro de <AppProvider>')
  return ctx
}

export { AUDIENCES, THEMES }
